export module ArrayUtils {

    // https://stackoverflow.com/a/16436975/2589276
    export function arraysEqual<T>(a: T[], b: T[]): boolean {
        if(a === b) return true
        if(a == undefined || b == undefined) return false
        if(a.length != b.length) return false

        for(let i = 0; i < a.length; i += 1) {
            if(a[i] !== b[i]) return false
        }
        return true
    }

    export function shuffle<T>(array: T[]): T[] {
        const tempArray = [...array]
        let counter = tempArray.length

        // While there are elements in the array
        while(counter > 0) {
            // Pick a random index
            const index = Math.floor(Math.random() * counter)

            // Decrease counter by 1
            counter -= 1

            // And swap the last element with it
            const temp = tempArray[counter]
            tempArray[counter] = tempArray[index]
            tempArray[index] = temp
        }
        return tempArray
    }

    export function arrayMean(array: number[]): number {
        const minimumNumberOfEntriesNeededToCalculateMean = 1
        const totalMeasurements = array.length
        if(array.length < minimumNumberOfEntriesNeededToCalculateMean) return 0
        return array.reduce((a, b) => a + b) / totalMeasurements
    }

    export function arrayStandardDeviation(array: number[], mean: number, totalMeasurements: number): number {
        const minimumNumberOfEntriesNeededToCalculateStandardDeviation = 2
        if(array.length < minimumNumberOfEntriesNeededToCalculateStandardDeviation) return 1
        return Math.sqrt(array.map((x) => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / (totalMeasurements - 1))
    }

    export function arrayStandardError(standardDeviation: number, totalMeasurements: number): number {
        return standardDeviation / Math.sqrt(totalMeasurements)
    }

    export function calculateConfidenceInterval95(array: number[]) {
        const standardDeviation = arrayStandardDeviation(array, ArrayUtils.arrayMean(array), array.length)

        return standardDeviation * 1.96 / Math.sqrt(array.length)
    }

    export function arrayPercentile(array: number[], percentile: number): number {
        array.sort((a, b) => a - b)

        const rank = (percentile / 100) * (array.length - 1)

        const lowerIndex = Math.floor(rank)
        const upperIndex = Math.ceil(rank)

        if(lowerIndex === upperIndex) {
            return array[lowerIndex]
        } else {
            const weight = rank - lowerIndex
            return array[lowerIndex] * (1 - weight) + array[upperIndex] * weight
        }
    }

    export function randomElementFromArray<T>(array: T[]): T {
        return array[Math.floor(Math.random() * array.length)]
    }

    export function randomElementsKeepInArray<T>(array: T[], newArrayLength: number): T[] {
        if(newArrayLength == array.length) return array
        const newArray = [...array]
        newArray.splice(newArray.indexOf(randomElementFromArray(newArray)), 1)
        return randomElementsKeepInArray(newArray, newArrayLength)
    }

    export function randomElementsThrowFromArray<T>(array: T[], amountToThrow: number): T[] {
        if(amountToThrow == 0) return array
        const newArray = [...array]
        newArray.splice(newArray.indexOf(randomElementFromArray(newArray)), 1)
        return randomElementsThrowFromArray(newArray, amountToThrow - 1)
    }

    export function removeElementFromArray<T>(array: T[], element: T): boolean {
        const index = array.indexOf(element)
        if(index > -1) {
            array.splice(index, 1)
            return true
        }
        return false
    }

    export function removeElementsFromArray<T>(array: T[], elements: T[]) {
        for(const element of elements) {
            removeElementFromArray(array, element)
        }
    }

    export function removeDuplicatesFromArray<T>(array: T[]): T[] {
        return array.filter((elem, index, self) => index == self.indexOf(elem))
    }

    export function removeAllOccurrenceFromArray<T>(array: T[], valueToRemove: T): T[] {
        return array.filter((element) => element !== valueToRemove)
    }

    // https://www.codegrepper.com/code-examples/typescript/javascript+Count+the+occurrences+of+a+value+in+an+array
    export function countOccurenceOfValueInArray<T>(array: T[], value: T): number {
        const countOccurrence = array.reduce((a, v) => (v === value ? a + 1 : a), 0)
        return countOccurrence
    }

    export function elementInArray<T>(array: T[], element: T): boolean {
        for(const arrayObject of array) {
            if(arrayObject == element) return true
        }
        return false
    }

    export function differenceOfTwoArrays<T>(array1: T[], array2: T[]): T[] {
        return array1.filter((character) => !array2.includes(character))
    }

    export function swapElementOfArray<T>(array: T[], index1: number, index2: number): T[] {
        const temp = array[index1]
        array[index1] = array[index2]
        array[index2] = temp
        return array
    }

    export function removeAllInstancesOfElementInArray<T>(array: T[], element: T): T[] {
        return array.filter((value) => value != element)
    }

    export function findIndexGreaterThanOrEqual<T extends string | number>(sortedArray: T[], thresholdValue: T): number {
        if(sortedArray.length == 0) return -1

        let startIndex = 0
        let endIndex = sortedArray.length - 1

        while(endIndex - startIndex > 1) {
            const middleIndex = Math.floor((startIndex + endIndex) / 2)
            if(sortedArray[middleIndex] < thresholdValue) startIndex = middleIndex
            else endIndex = middleIndex
        }

        if(sortedArray[startIndex] >= thresholdValue) return startIndex
        if(sortedArray[endIndex] >= thresholdValue) return endIndex
        if(sortedArray[endIndex] < thresholdValue) return -1
        return startIndex
    }

    export function findMaximumIndexLessThanOrEqual<T extends string | number>(sortedArray: T[], thresholdValue: T): number {
        if(sortedArray.length == 0) return -1

        let startIndex = 0
        let endIndex = sortedArray.length - 1

        while(endIndex - startIndex > 1) {
            const middleIndex = Math.floor((startIndex + endIndex) / 2)
            if(sortedArray[middleIndex] <= thresholdValue) startIndex = middleIndex
            else endIndex = middleIndex
        }

        if(sortedArray[endIndex] <= thresholdValue) return endIndex
        if(sortedArray[startIndex] <= thresholdValue) return startIndex
        if(sortedArray[endIndex] > thresholdValue) return -1
        return startIndex
    }

    export function getNElementIfExistsOrClosestLower<T>(array: T[], index: number): T | undefined {
        if(array.length == 0) return undefined
        if(array.length <= index) return array[array.length - 1]
        return array[index]
    }

    export function sortNumberArrayAscending(array: number[]): number[] {
        return array.sort((a, b) => a - b)
    }

    export function sortNumberArrayDescending(array: number[]): number[] {
        return array.sort((a, b) => b - a)
    }

    export function isSomeElementIncluded<T>(array: T[], elements: T[]): boolean {
        return elements.some((element) => array.includes(element))
    }

    export function getUniqueElements<T>(array: T[]): T[] {
        return Array.from(new Set(array))
    }
}
